import type { ComponentType, ReactNode } from 'react';
import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { useGuide } from './api';
import type { Guide } from './types';
import { isEmptyGuide } from './types';

/**
 * The gate both embeds run before a guide renders: still loading, failed to
 * load, or nothing to show. Only the placement differs — the paginated embed
 * pins the message over its fixed viewport, the full embed lets it flow — so
 * each passes its own wrapper.
 */
export function GuideGate({
   wrapper: Wrapper,
   children,
}: {
   wrapper: ComponentType<{ children: ReactNode; role: 'status' | 'alert' }>;
   children: (guide: Guide) => ReactNode;
}) {
   const { data: guide, isPending, isError } = useGuide();

   if (isPending) {
      return (
         <Wrapper role="status">
            <FaIcon name="spinner" className="animate-spin text-2xl" aria-hidden />
            <span>{_js('Loading guide…')}</span>
         </Wrapper>
      );
   }

   if (isError || !guide) {
      return (
         <Wrapper role="alert">
            <p>{_js('We couldn’t load this guide.')}</p>
            <button
               type="button"
               className="rounded-lg bg-gray-100 px-4 py-2 font-semibold hover:bg-gray-200"
               onClick={() => window.location.reload()}
            >
               {_js('Try again')}
            </button>
         </Wrapper>
      );
   }

   if (isEmptyGuide(guide)) {
      return (
         <Wrapper role="status">
            <p>{_js('This guide doesn’t have any steps yet.')}</p>
         </Wrapper>
      );
   }

   return children(guide);
}
